import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import SystemHealthTab from "../components/admin/SystemHealthTab";
import PendingVerifyTab from "../components/admin/PendingVerifyTab";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
    Users, Lightbulb, ChatCircleText, ThumbsUp, Warning, ShieldStar,
    Trash, Pencil, Plus, Star, EyeSlash, Eye, Prohibit, CheckSquare,
    Broom, ArrowsClockwise, Pulse, Crown, UserPlus, DotsSixVertical,
    CaretDown,
} from "@phosphor-icons/react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import { COUNTRIES } from "../lib/countries";

function StatCard({ icon: Icon, label, value, color }) {
    return (
        <div className="rounded-2xl border border-border bg-card p-5">
            <div className={`h-10 w-10 rounded-lg grid place-items-center mb-3 ${color}`}>
                <Icon size={20} weight="duotone" className="text-white" />
            </div>
            <div className="font-display font-black text-3xl tracking-tight">{value}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mt-1">{label}</div>
        </div>
    );
}

function MaintenanceCard() {
    const [dedupRunning, setDedupRunning] = useState(false);
    const [dedupResult, setDedupResult] = useState(null);
    const [reseedRunning, setReseedRunning] = useState(false);
    const [version, setVersion] = useState(null);

    useEffect(() => {
        api.get("/version").then((r) => setVersion(r.data?.version || "unknown")).catch(() => setVersion("?"));
    }, []);

    const runDedup = async () => {
        if (!window.confirm("Remove duplicate admin-seeded ideas? Safe and reversible only via reseed.")) return;
        setDedupRunning(true);
        setDedupResult(null);
        try {
            const { data } = await api.post("/admin/dedup-posts");
            setDedupResult(data);
            toast.success(
                data.redundant_rows_before === 0
                    ? "No duplicates found — DB already clean."
                    : `Removed ${data.redundant_rows_before} duplicate rows across ${data.buckets_before} groups.`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Dedup failed");
        } finally {
            setDedupRunning(false);
        }
    };

    const runReseed = async () => {
        if (!window.confirm("Re-run the full admin seed? Re-inserts missing seed posts and runs cleanup. Safe to run anytime.")) return;
        setReseedRunning(true);
        try {
            const { data } = await api.post("/admin/reseed");
            const counts = Object.entries(data?.posts_by_country || {})
                .map(([c, n]) => `${c}: ${n}`).join(", ");
            toast.success(`Re-seed complete. ${counts}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Reseed failed");
        } finally {
            setReseedRunning(false);
        }
    };

    return (
        <div className="rounded-2xl border border-border bg-card p-6 mb-6" data-testid="admin-maintenance">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div>
                    <h3 className="font-display font-bold text-lg">Maintenance</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Backend version: <span className="font-mono">{version || "loading…"}</span>
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button
                        onClick={runDedup}
                        disabled={dedupRunning}
                        variant="outline"
                        data-testid="admin-dedup-btn"
                    >
                        <Broom size={16} weight="duotone" className="mr-1.5" />
                        {dedupRunning ? "Cleaning…" : "Remove duplicate ideas"}
                    </Button>
                    <Button
                        onClick={runReseed}
                        disabled={reseedRunning}
                        variant="outline"
                        data-testid="admin-reseed-btn"
                    >
                        <ArrowsClockwise size={16} weight="duotone" className="mr-1.5" />
                        {reseedRunning ? "Re-seeding…" : "Re-run seed"}
                    </Button>
                </div>
            </div>
            {dedupResult && (
                <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap" data-testid="admin-dedup-result">
                    {JSON.stringify(dedupResult, null, 2)}
                </div>
            )}
        </div>
    );
}

function StatsTab() {
    const [stats, setStats] = useState(null);
    const [err, setErr] = useState(null);
    const load = useCallback(() => {
        setErr(null); setStats(null);
        api.get("/admin/stats", { timeout: 15000 })
            .then(r => setStats(r.data))
            .catch(e => setErr(e?.message || "Failed to load stats"));
    }, []);
    useEffect(() => { load(); }, [load]);
    if (err) {
        return (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 text-sm" data-testid="admin-stats-error">
                <div className="font-semibold mb-2">Couldn't load stats</div>
                <div className="text-muted-foreground mb-3">{err}</div>
                <Button size="sm" onClick={load} data-testid="admin-stats-retry">Retry</Button>
            </div>
        );
    }
    if (!stats) {
        return (
            <div className="space-y-6" data-testid="admin-stats-loading">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-24 rounded-2xl border border-border bg-muted/40 animate-pulse" />
                    ))}
                </div>
                <div className="h-48 rounded-2xl border border-border bg-muted/30 animate-pulse" />
            </div>
        );
    }
    return (
        <div data-testid="admin-stats-tab">
            <MaintenanceCard />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <StatCard icon={Users} label="Users" value={stats.total_users} color="bg-[#6C63FF]" />
                <StatCard icon={Lightbulb} label="Ideas" value={stats.total_posts} color="bg-[#FF8A00]" />
                <StatCard icon={ChatCircleText} label="Comments" value={stats.total_comments} color="bg-emerald-500" />
                <StatCard icon={ThumbsUp} label="Likes" value={stats.total_likes} color="bg-pink-500" />
                <StatCard icon={Warning} label="Reported" value={stats.reported_posts} color="bg-red-500" />
                <StatCard icon={Prohibit} label="Blocked" value={stats.blocked_users} color="bg-zinc-700" />
            </div>

            <h3 className="font-display font-bold text-lg mb-3" data-testid="engagement-heading">
                User engagement
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="engagement-cards">
                <StatCard icon={Pulse} label="DAU · 24h" value={stats.dau ?? 0} color="bg-[#6C63FF]" />
                <StatCard icon={Pulse} label="WAU · 7d" value={stats.wau ?? 0} color="bg-[#6C63FF]/80" />
                <StatCard icon={Pulse} label="MAU · 30d" value={stats.mau ?? 0} color="bg-[#6C63FF]/60" />
                <StatCard icon={UserPlus} label="New · 7d" value={stats.new_users_7d ?? 0} color="bg-emerald-500" />
                <StatCard icon={UserPlus} label="New · 30d" value={stats.new_users_30d ?? 0} color="bg-emerald-600" />
            </div>

            <h3 className="font-display font-bold text-lg mb-3" data-testid="monetisation-heading">
                Monetisation
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="monetisation-cards">
                <StatCard icon={Crown} label="Paid (Pro)" value={stats.paid_users ?? 0} color="bg-[#FF8A00]" />
                <StatCard icon={Users} label="Free" value={stats.free_users ?? 0} color="bg-zinc-500" />
                <StatCard
                    icon={Crown}
                    label="Pro conversion"
                    value={
                        stats.total_users
                            ? `${((stats.paid_users / stats.total_users) * 100).toFixed(1)}%`
                            : "—"
                    }
                    color="bg-[#FF8A00]/70"
                />
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display font-bold text-lg mb-4">Users by country</h3>
                <div className="space-y-2">
                    {stats.users_by_country.map((r) => {
                        const max = Math.max(1, ...stats.users_by_country.map(x => x.count));
                        return (
                            <div key={r.country} className="flex items-center gap-3">
                                <div className="w-32 text-sm font-semibold truncate">{r.country}</div>
                                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                    <div className="h-full bg-[#6C63FF]" style={{ width: `${(r.count / max) * 100}%` }} />
                                </div>
                                <div className="w-10 text-right text-sm font-bold">{r.count}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function UsersTab() {
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [paidCount, setPaidCount] = useState(0);
    const [freeCount, setFreeCount] = useState(0);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [country, setCountry] = useState("");
    const [subscription, setSubscription] = useState("all");
    const [skip, setSkip] = useState(0);
    // Edit dialog state. `editing` is the user object being modified;
    // `editForm` mirrors the editable subset of fields.
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({ country: "", pricing_country: "", subscription: "free" });
    const [savingEdit, setSavingEdit] = useState(false);
    // Credit-adjust dialog state. Separate from edit so admins can pop
    // it open per-row without losing the search/filter selection.
    const [creditTarget, setCreditTarget] = useState(null);
    const [creditDelta, setCreditDelta] = useState("");
    const [creditReason, setCreditReason] = useState("");
    const [savingCredits, setSavingCredits] = useState(false);
    // 2026-02: bumped from 15 → 50 per user request. Admin can now see
    // 50 users per page; still keeps DB pressure low (server caps at 200).
    const limit = 50;

    const load = async () => {
        const params = {
            q: q || undefined,
            status,
            country: country || undefined,
            subscription,
            limit,
            skip,
        };
        const { data } = await api.get("/admin/users", { params });
        setUsers(data.items); setTotal(data.total);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status, country, subscription, skip]);

    // Always fetch the overall paid / free split (independent of current
    // filter) so the summary line stays stable when the operator narrows
    // the table by country, status, etc.
    useEffect(() => {
        let cancelled = false;
        api.get("/admin/stats").then((r) => {
            if (cancelled) return;
            setPaidCount(r.data?.paid_users || 0);
            setFreeCount(r.data?.free_users || 0);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const block = async (u) => {
        const url = u.blocked ? `/admin/users/${u.id}/unblock` : `/admin/users/${u.id}/block`;
        await api.post(url);
        toast.success(u.blocked ? "Unblocked" : "Blocked");
        load();
    };
    const del = async (u) => {
        if (!window.confirm(`Delete ${u.email}?`)) return;
        await api.delete(`/admin/users/${u.id}`);
        toast.success("Deleted"); load();
    };

    // Inline subscription tier change — used by the dropdown on the
    // Plan column. Lets admins flip a user between Free / Pro Local /
    // Pro Global in one click during testing, without opening the full
    // edit dialog. Server already validates the literal set.
    const setTier = async (u, tier) => {
        const current = (u.subscription || "free").toLowerCase();
        // "pro" (legacy) is functionally pro_global; treat that as the
        // same tier so we don't no-op or churn the row.
        const normalisedCurrent = current === "pro" ? "pro_global" : current;
        if (normalisedCurrent === tier) return;
        try {
            await api.patch(`/admin/users/${u.id}`, { subscription: tier });
            const label = tier === "free" ? "Free"
                : tier === "pro_local" ? "Pro · Local"
                : "Pro · Global";
            toast.success(`${u.email} → ${label}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    // 1 credit = 10,000 tokens (mirrors core/credits.py TOKENS_PER_CREDIT).
    // The backend is the only source of truth — this is only for the
    // table-cell display so the admin doesn't have to do the math.
    const TOKENS_PER_CREDIT = 10000;
    const creditsOf = (u) => Math.floor((u?.chat_tokens_balance || 0) / TOKENS_PER_CREDIT);

    // Open the credit-adjust dialog for a single user. State is local
    // to this tab so opening it doesn't disturb search/filter inputs.
    const openCreditDialog = (u) => {
        setCreditTarget(u);
        setCreditDelta("");
        setCreditReason("");
    };

    // Apply a credit delta. `delta` is in CREDITS (UI unit), negative
    // numbers subtract. Optimistically updates the row, falls back to
    // a fresh load on success or failure.
    const applyCreditDelta = async (delta) => {
        if (!creditTarget || !delta) return;
        setSavingCredits(true);
        try {
            const { data } = await api.post(`/admin/users/${creditTarget.id}/credits`, {
                delta_credits: Number(delta),
                reason: creditReason.trim() || undefined,
            });
            toast.success(
                delta > 0
                    ? `Added ${delta} credits → balance ${data.credits_after}`
                    : `Removed ${Math.abs(delta)} credits → balance ${data.credits_after}`,
            );
            setCreditTarget(null);
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not adjust credits";
            toast.error(typeof msg === "string" ? msg : "Could not adjust credits");
        } finally {
            setSavingCredits(false);
        }
    };

    return (
        <div data-testid="admin-users-tab">
            <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="user-summary">
                <div className="text-sm">
                    <span className="font-bold">{total}</span>{" "}
                    <span className="text-muted-foreground">
                        match{total === 1 ? "es" : ""} current filter
                    </span>
                </div>
                <div className="text-xs text-muted-foreground">·</div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF8A00]/10 text-[#FF8A00] text-xs font-bold" data-testid="user-summary-paid">
                    <Crown size={12} weight="fill" />
                    {paidCount} paid
                </div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-muted-foreground text-xs font-bold" data-testid="user-summary-free">
                    {freeCount} free
                </div>
            </div>
            <div className="flex flex-wrap gap-3 mb-4">
                <Input placeholder="Search name / email…" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }}
                    className="max-w-sm" data-testid="admin-users-search" />
                <Select value={status} onValueChange={(v) => { setStatus(v); setSkip(0); }}>
                    <SelectTrigger className="w-40" data-testid="admin-users-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="blocked">Blocked</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={country || "any"} onValueChange={(v) => { setCountry(v === "any" ? "" : v); setSkip(0); }}>
                    <SelectTrigger className="w-44" data-testid="admin-users-country"><SelectValue placeholder="Country" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                        <SelectItem value="any">Any country</SelectItem>
                        {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={subscription} onValueChange={(v) => { setSubscription(v); setSkip(0); }}>
                    <SelectTrigger className="w-44" data-testid="admin-users-subscription"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All plans</SelectItem>
                        <SelectItem value="paid">Paid (any Pro)</SelectItem>
                        <SelectItem value="pro_global">Pro · Global</SelectItem>
                        <SelectItem value="pro_local">Pro · Local</SelectItem>
                        <SelectItem value="free">Free</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left px-4 py-3">Name</th>
                                <th className="text-left px-4 py-3">Email</th>
                                <th className="text-left px-4 py-3">Country</th>
                                <th className="text-left px-4 py-3">Plan</th>
                                <th className="text-right px-4 py-3">Credits</th>
                                <th className="text-left px-4 py-3">Joined</th>
                                <th className="text-right px-4 py-3">Posts</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-right px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => {
                                const sub = (u.subscription || "free").toLowerCase();
                                const isProGlobal = sub === "pro_global" || sub === "pro";
                                const isProLocal  = sub === "pro_local";
                                return (
                                <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.id}`}>
                                    <td className="px-4 py-3 font-semibold">{u.name}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                                    <td className="px-4 py-3">{u.country}</td>
                                    <td className="px-4 py-3" data-testid={`user-plan-${u.id}`}>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button
                                                    type="button"
                                                    data-testid={`plan-trigger-${u.id}`}
                                                    title="Change subscription tier"
                                                    className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
                                                >
                                                    {isProGlobal ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#FF8A00]/15 text-[#FF8A00] text-xs font-bold">
                                                            <Crown size={11} weight="fill" /> Pro · Global
                                                        </span>
                                                    ) : isProLocal ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#6C63FF]/15 text-[#6C63FF] text-xs font-bold">
                                                            <Crown size={11} weight="fill" /> Pro · Local
                                                        </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 rounded-md bg-muted text-muted-foreground text-xs font-bold">
                                                            Free
                                                        </span>
                                                    )}
                                                    <CaretDown size={10} className="text-muted-foreground" />
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="start" className="w-44">
                                                <DropdownMenuItem
                                                    onClick={() => setTier(u, "free")}
                                                    data-testid={`set-tier-free-${u.id}`}
                                                    disabled={!isProGlobal && !isProLocal}
                                                >
                                                    Downgrade to Free
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={() => setTier(u, "pro_local")}
                                                    data-testid={`set-tier-pro-local-${u.id}`}
                                                    disabled={isProLocal}
                                                >
                                                    <Crown size={12} weight="fill" className="mr-1.5 text-[#6C63FF]" />
                                                    Set Pro · Local
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={() => setTier(u, "pro_global")}
                                                    data-testid={`set-tier-pro-global-${u.id}`}
                                                    disabled={isProGlobal}
                                                >
                                                    <Crown size={12} weight="fill" className="mr-1.5 text-[#FF8A00]" />
                                                    Set Pro · Global
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            type="button"
                                            onClick={() => openCreditDialog(u)}
                                            data-testid={`adjust-credits-${u.id}`}
                                            title="Adjust credits"
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border hover:border-[#6C63FF] hover:bg-[#6C63FF]/5 transition-colors text-xs font-bold tabular-nums"
                                        >
                                            <span data-testid={`credits-value-${u.id}`}>{creditsOf(u)}</span>
                                            <Pencil size={11} className="text-muted-foreground" />
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 text-right">{u.posts_count}</td>
                                    <td className="px-4 py-3">
                                        {u.blocked ? <span className="text-red-500 font-semibold">Blocked</span>
                                            : <span className="text-emerald-500 font-semibold">Active</span>}
                                    </td>
                                    <td className="px-4 py-3 text-right space-x-1">
                                        <Button size="sm" variant="ghost" onClick={() => {
                                            setEditing(u);
                                            setEditForm({
                                                country: u.country || "",
                                                pricing_country: u.pricing_country || u.country || "",
                                                subscription: u.subscription || "free",
                                            });
                                        }} data-testid={`edit-user-${u.id}`} title="Edit user">
                                            <Pencil size={14} />
                                        </Button>
                                        {u.role !== "admin" && (
                                            <>
                                                <Button size="sm" variant="ghost" onClick={() => block(u)} data-testid={`block-${u.id}`} title={u.blocked ? "Unblock user" : "Block user"}>
                                                    {u.blocked ? <CheckSquare size={14} /> : <Prohibit size={14} />}
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => del(u)} className="text-red-500" data-testid={`delete-user-${u.id}`} title="Delete user">
                                                    <Trash size={14} />
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between p-3 border-t border-border text-sm">
                    <div className="text-muted-foreground" data-testid="admin-users-page-info">
                        Page <b>{Math.floor(skip / limit) + 1}</b> of <b>{Math.max(1, Math.ceil(total / limit))}</b>
                        {" · "}{total} users
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="admin-users-prev">Prev</Button>
                        <Button size="sm" variant="outline" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="admin-users-next">Next</Button>
                    </div>
                </div>
            </div>

            {/*
              Admin user-edit dialog.
              Lets the admin change a user's `country`, `pricing_country`,
              and `subscription`. This dialog is intentionally the only
              way to mutate `pricing_country` server-side; signup-time
              anti-abuse logic still locks it at registration based on
              IP geolocation.
            */}
            <Dialog open={!!creditTarget} onOpenChange={(open) => { if (!open) setCreditTarget(null); }}>
                <DialogContent className="max-w-md" data-testid="adjust-credits-dialog">
                    <DialogHeader>
                        <DialogTitle>Adjust credits · {creditTarget?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2 text-sm">
                        <div className="text-muted-foreground text-xs">{creditTarget?.email}</div>
                        <div className="rounded-xl border border-border bg-muted/30 p-4 flex items-baseline justify-between">
                            <span className="text-xs uppercase tracking-wider text-muted-foreground">Current balance</span>
                            <span className="text-2xl font-bold tabular-nums" data-testid="current-credits">
                                {creditsOf(creditTarget)}
                                <span className="text-xs font-normal text-muted-foreground ml-1">credits</span>
                            </span>
                        </div>
                        <div>
                            <Label className="text-xs font-bold mb-2 block">Quick add</Label>
                            <div className="flex gap-2 flex-wrap">
                                {[5, 25, 100, 500].map((n) => (
                                    <Button
                                        key={`add-${n}`}
                                        size="sm"
                                        variant="outline"
                                        disabled={savingCredits}
                                        onClick={() => applyCreditDelta(n)}
                                        data-testid={`quick-add-${n}`}
                                        className="hover:border-emerald-500 hover:text-emerald-500"
                                    >
                                        + {n}
                                    </Button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-bold mb-2 block">Quick remove</Label>
                            <div className="flex gap-2 flex-wrap">
                                {[5, 25, 100, 500].map((n) => (
                                    <Button
                                        key={`sub-${n}`}
                                        size="sm"
                                        variant="outline"
                                        disabled={savingCredits || creditsOf(creditTarget) === 0}
                                        onClick={() => applyCreditDelta(-n)}
                                        data-testid={`quick-sub-${n}`}
                                        className="hover:border-rose-500 hover:text-rose-500"
                                    >
                                        − {n}
                                    </Button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-bold mb-1.5 block">Custom amount</Label>
                            <div className="flex gap-2">
                                <Input
                                    type="number"
                                    placeholder="e.g. 250 (or -250 to subtract)"
                                    value={creditDelta}
                                    onChange={(e) => setCreditDelta(e.target.value)}
                                    data-testid="credit-delta-input"
                                    className="flex-1"
                                />
                                <Button
                                    onClick={() => applyCreditDelta(creditDelta)}
                                    disabled={savingCredits || !creditDelta || Number(creditDelta) === 0}
                                    data-testid="credit-delta-apply"
                                >
                                    Apply
                                </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Positive adds, negative subtracts. Balance is clamped at 0.
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs font-bold mb-1.5 block">Reason <span className="text-muted-foreground font-normal">(optional, audit trail)</span></Label>
                            <Input
                                placeholder="e.g. testing chat flow, refund for bug XYZ…"
                                value={creditReason}
                                onChange={(e) => setCreditReason(e.target.value)}
                                data-testid="credit-reason-input"
                                maxLength={200}
                            />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
                <DialogContent className="max-w-md" data-testid="edit-user-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit user · {editing?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2 text-sm">
                        <div className="text-muted-foreground text-xs">{editing?.email}</div>
                        <div>
                            <Label className="text-xs font-bold mb-1.5 block">Country</Label>
                            <Select value={editForm.country || "India"}
                                onValueChange={(v) => setEditForm((f) => ({ ...f, country: v }))}>
                                <SelectTrigger data-testid="edit-user-country"><SelectValue /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                What the user picked as their home country. Affects which feed they see by default.
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs font-bold mb-1.5 block">Pricing country</Label>
                            <Select value={editForm.pricing_country || "India"}
                                onValueChange={(v) => setEditForm((f) => ({ ...f, pricing_country: v }))}>
                                <SelectTrigger data-testid="edit-user-pricing-country"><SelectValue /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Drives currency shown on the pricing page. Override sparingly — usually only
                                for legit travellers who signed up from the wrong country.
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs font-bold mb-1.5 block">Subscription tier</Label>
                            <Select value={editForm.subscription}
                                onValueChange={(v) => setEditForm((f) => ({ ...f, subscription: v }))}>
                                <SelectTrigger data-testid="edit-user-subscription"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="free">Free</SelectItem>
                                    <SelectItem value="pro_local">Pro · Local (home country only)</SelectItem>
                                    <SelectItem value="pro_global">Pro · Global (all countries)</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Grant or revoke Pro manually. Pro Local unlocks all categories in
                                the user's home country only; Pro Global unlocks every country.
                                Use for support escalations after a refund or a genuinely-paid
                                order whose webhook never landed.
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-user-cancel">
                            Cancel
                        </Button>
                        <Button disabled={savingEdit} data-testid="edit-user-save"
                            onClick={async () => {
                                if (!editing) return;
                                setSavingEdit(true);
                                try {
                                    await api.patch(`/admin/users/${editing.id}`, editForm);
                                    toast.success("User updated");
                                    setEditing(null);
                                    load();
                                    // Refresh paid/free summary in case sub changed.
                                    api.get("/admin/stats").then((r) => {
                                        setPaidCount(r.data?.paid_users || 0);
                                        setFreeCount(r.data?.free_users || 0);
                                    }).catch(() => {});
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Update failed");
                                } finally {
                                    setSavingEdit(false);
                                }
                            }}>
                            {savingEdit ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function PostsTab() {
    const [posts, setPosts] = useState([]);
    const [total, setTotal] = useState(0);
    const [byCategory, setByCategory] = useState([]);
    const [countries, setCountries] = useState([]);
    const [reported, setReported] = useState(false);
    const [category, setCategory] = useState("all");
    const [country, setCountry] = useState("all");
    const [authorRole, setAuthorRole] = useState("all");
    const [q, setQ] = useState("");
    const [skip, setSkip] = useState(0);
    const [categories, setCategories] = useState([]);
    const [editPost, setEditPost] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const [editDesc, setEditDesc] = useState("");
    const [editCat, setEditCat] = useState("");
    const [saving, setSaving] = useState(false);
    const [dragId, setDragId] = useState(null);
    const [overId, setOverId] = useState(null);
    // Drag-to-reorder is only meaningful when the admin has narrowed the
    // list to a SINGLE (country, category) slice of admin-curated posts.
    // Outside that slice the order doesn't even apply to the user-facing
    // Home feed (which always asks the server for a specific country +
    // category), so we keep the UI honest by gating the handles.
    const dragEnabled = authorRole === "admin" && country !== "all" && category !== "all";
    const limit = dragEnabled ? 500 : 15;

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    const load = async () => {
        const params = { reported, limit, skip };
        if (category !== "all") params.category = category;
        if (country !== "all") params.country = country;
        if (authorRole !== "all") params.author_role = authorRole;
        if (q) params.q = q;
        const { data } = await api.get("/admin/posts", { params });
        setPosts(data.items);
        setTotal(data.total);
        setByCategory(data.by_category || []);
        // Countries list comes from the server based on actual data; only
        // overwrite local state on the very first load (when filter is
        // "all") so the dropdown options don't shrink as the user narrows
        // their filters and zero out categories.
        if (country === "all" && Array.isArray(data.countries) && data.countries.length) {
            setCountries(data.countries);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [reported, category, country, authorRole, q, skip]);

    const feature = async (p) => { await api.post(`/admin/posts/${p.id}/feature`); toast.success("Featured toggled"); load(); };
    const hide = async (p) => { await api.post(`/admin/posts/${p.id}/hide`); toast.success("Visibility toggled"); load(); };
    const del = async (p) => { if (!window.confirm("Delete post?")) return; await api.delete(`/admin/posts/${p.id}`); toast.success("Deleted"); load(); };
    const startEdit = (p) => {
        setEditPost(p);
        setEditTitle(p.title || "");
        setEditDesc(p.description || "");
        setEditCat(p.category || "");
    };
    const closeEdit = () => { setEditPost(null); setSaving(false); };
    // ── Drag-and-drop reorder ────────────────────────────────────────
    // We use the browser's native HTML5 DnD so this works with zero new
    // dependencies. The hover row's index moves the dragged row inline
    // (optimistic re-render); we only POST to /admin/posts/reorder on
    // `drop` to avoid one PATCH per pixel of mouse movement.
    const onDragStart = (e, id) => {
        if (!dragEnabled) return;
        setDragId(id);
        e.dataTransfer.effectAllowed = "move";
        // Firefox requires dataTransfer.setData to actually start a drag.
        try { e.dataTransfer.setData("text/plain", id); } catch (_) { /* noop */ }
    };
    const onDragOver = (e, overPostId) => {
        if (!dragEnabled || !dragId || dragId === overPostId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (overId !== overPostId) setOverId(overPostId);
    };
    const onDragEnd = () => { setDragId(null); setOverId(null); };
    const onDrop = async (e, dropOnId) => {
        if (!dragEnabled || !dragId || dragId === dropOnId) {
            onDragEnd(); return;
        }
        e.preventDefault();
        const fromIdx = posts.findIndex((p) => p.id === dragId);
        const toIdx = posts.findIndex((p) => p.id === dropOnId);
        if (fromIdx === -1 || toIdx === -1) { onDragEnd(); return; }
        const next = posts.slice();
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        setPosts(next);  // optimistic
        onDragEnd();
        try {
            await api.post("/admin/posts/reorder", {
                country, category,
                ordered_ids: next.map((p) => p.id),
            });
            toast.success(`Moved "${moved.title.slice(0, 40)}" to #${toIdx + 1}`);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not save order";
            toast.error(typeof msg === "string" ? msg : "Could not save order");
            load();  // roll back via fresh fetch
        }
    };
    const saveEdit = async () => {
        if (!editPost) return;
        if (!editTitle.trim() || !editDesc.trim()) {
            toast.error("Title and description are required"); return;
        }
        setSaving(true);
        try {
            await api.patch(`/admin/posts/${editPost.id}`, {
                title: editTitle.trim(),
                description: editDesc.trim(),
                category: editCat || undefined,
            });
            toast.success("Post updated");
            closeEdit();
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not save";
            toast.error(typeof msg === "string" ? msg : "Could not save");
        } finally { setSaving(false); }
    };

    return (
        <div data-testid="admin-posts-tab">
            {/* Category overview chips */}
            {byCategory.length > 0 && (
                <div className="mb-4 -mx-1 flex flex-wrap gap-1.5" data-testid="admin-category-chips">
                    <button
                        onClick={() => { setCategory("all"); setSkip(0); }}
                        data-testid="chip-all"
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
                            ${category === "all" ? "bg-[#6C63FF] text-white" : "bg-card border border-border hover:border-[#6C63FF]/40"}`}
                    >
                        All <span className="opacity-70 ml-1">{byCategory.reduce((s, c) => s + c.count, 0)}</span>
                    </button>
                    {byCategory.map((c) => (
                        <button
                            key={c.category}
                            onClick={() => { setCategory(c.category); setSkip(0); }}
                            data-testid={`chip-${c.category}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
                                ${category === c.category ? "bg-[#6C63FF] text-white" : "bg-card border border-border hover:border-[#6C63FF]/40"}`}
                        >
                            {c.category} <span className="opacity-70 ml-1">{c.count}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <Input
                    placeholder="Search title / description / author…"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setSkip(0); }}
                    className="max-w-sm"
                    data-testid="admin-posts-search"
                />
                <Select value={category} onValueChange={(v) => { setCategory(v); setSkip(0); }}>
                    <SelectTrigger className="w-52" data-testid="admin-posts-category">
                        <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                        <SelectItem value="all">All categories</SelectItem>
                        {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={authorRole} onValueChange={(v) => { setAuthorRole(v); setSkip(0); }}>
                    <SelectTrigger className="w-40" data-testid="admin-posts-author"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All authors</SelectItem>
                        <SelectItem value="admin">Admin only</SelectItem>
                        <SelectItem value="user">Users only</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={country} onValueChange={(v) => { setCountry(v); setSkip(0); }}>
                    <SelectTrigger className="w-44" data-testid="admin-posts-country">
                        <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                        <SelectItem value="all">All countries</SelectItem>
                        {countries.map((c) => (
                            <SelectItem key={c} value={c} data-testid={`country-opt-${c}`}>{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="flex items-center gap-2 ml-auto">
                    <Switch checked={reported} onCheckedChange={(v) => { setReported(v); setSkip(0); }} data-testid="reported-toggle" />
                    <Label className="m-0">Reported only</Label>
                </div>
            </div>

            <div className="space-y-3">
                {/* Drag-to-reorder hint / prerequisite banner. Only shown for
                    the admin-curated slice because the manual order ONLY
                    applies to the curated Home feed. */}
                {authorRole === "admin" && !dragEnabled && (
                    <div
                        className="rounded-xl border border-dashed border-[#6C63FF]/30 bg-[#6C63FF]/5 p-3 text-xs text-muted-foreground flex items-center gap-2"
                        data-testid="reorder-hint"
                    >
                        <DotsSixVertical size={16} className="text-[#6C63FF]" weight="bold" />
                        Pick a specific <b className="text-foreground">country</b> AND <b className="text-foreground">category</b> to enable drag-and-drop reorder.
                    </div>
                )}
                {dragEnabled && (
                    <div
                        className="rounded-xl border border-[#6C63FF]/30 bg-[#6C63FF]/5 p-3 text-xs flex items-center gap-2"
                        data-testid="reorder-active"
                    >
                        <DotsSixVertical size={16} className="text-[#6C63FF]" weight="bold" />
                        Drag any row by the handle to change its position in <b>{country}</b> · <b>{category}</b>. Order saves instantly and is what live users see.
                    </div>
                )}
                {posts.length === 0 && (
                    <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
                        No posts match these filters.
                    </div>
                )}
                {posts.map((p, idx) => (
                    <div
                        key={p.id}
                        draggable={dragEnabled}
                        onDragStart={(e) => onDragStart(e, p.id)}
                        onDragOver={(e) => onDragOver(e, p.id)}
                        onDrop={(e) => onDrop(e, p.id)}
                        onDragEnd={onDragEnd}
                        className={`rounded-xl border bg-card p-4 flex flex-wrap gap-3 items-center transition-colors
                            ${overId === p.id && dragId && dragId !== p.id ? "border-[#6C63FF] ring-2 ring-[#6C63FF]/30" : "border-border"}
                            ${dragId === p.id ? "opacity-40" : ""}`}
                        data-testid={`admin-post-${p.id}`}
                    >
                        {dragEnabled && (
                            <button
                                type="button"
                                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-[#6C63FF] touch-none"
                                title="Drag to reorder"
                                data-testid={`drag-handle-${p.id}`}
                                aria-label={`Drag ${p.title} to reorder`}
                            >
                                <DotsSixVertical size={20} weight="bold" />
                            </button>
                        )}
                        {dragEnabled && (
                            <span
                                className="text-xs font-bold text-muted-foreground tabular-nums min-w-[2ch]"
                                data-testid={`position-${p.id}`}
                            >
                                #{idx + 1}
                            </span>
                        )}
                        <div className="flex-1 min-w-0">
                            <Link to={`/post/${p.id}`} className="font-display font-bold hover:text-[#6C63FF] block truncate">{p.title}</Link>
                            <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                                <span className="font-semibold text-[#6C63FF]">{p.category}</span>
                                <span>·</span>
                                <span>{p.author_name}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider
                                    ${p.author_role === "admin" ? "bg-[#6C63FF]/10 text-[#6C63FF]" : "bg-muted text-muted-foreground"}`}>
                                    {p.author_role || "user"}
                                </span>
                                <span>·</span>
                                <span>{p.country}</span>
                            </div>
                        </div>
                        {p.featured && <span className="text-xs font-bold text-[#FF8A00]">FEATURED</span>}
                        {p.hidden && <span className="text-xs font-bold text-zinc-500">HIDDEN</span>}
                        {(p.reports?.length || 0) > 0 && <span className="text-xs font-bold text-red-500">{p.reports.length} reports</span>}
                        <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`edit-post-${p.id}`}>
                            <Pencil size={14} className="mr-1" />Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => feature(p)} data-testid={`feature-${p.id}`}>
                            <Star size={14} className="mr-1" />{p.featured ? "Unfeature" : "Feature"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => hide(p)} data-testid={`hide-${p.id}`}>
                            {p.hidden ? <Eye size={14} /> : <EyeSlash size={14} />}
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-500" onClick={() => del(p)} data-testid={`delete-post-${p.id}`}>
                            <Trash size={14} />
                        </Button>
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-between p-3 mt-3 text-sm">
                <div className="text-muted-foreground" data-testid="admin-posts-page-info">
                    {dragEnabled ? (
                        <>Showing all <b>{posts.length}</b> · drag to reorder</>
                    ) : (
                        <>
                            Page <b>{Math.floor(skip / limit) + 1}</b> of <b>{Math.max(1, Math.ceil(total / limit))}</b>
                            {" · "}{total} posts
                        </>
                    )}
                </div>
                {!dragEnabled && (
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="admin-posts-prev">Prev</Button>
                        <Button size="sm" variant="outline" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="admin-posts-next">Next</Button>
                    </div>
                )}
            </div>

            <Dialog open={!!editPost} onOpenChange={(open) => { if (!open) closeEdit(); }}>
                <DialogContent className="max-w-2xl" data-testid="edit-post-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit post</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="edit-title">Title</Label>
                            <Input
                                id="edit-title"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                data-testid="edit-post-title"
                                placeholder="Idea title"
                            />
                        </div>
                        <div>
                            <Label htmlFor="edit-desc">Description</Label>
                            <Textarea
                                id="edit-desc"
                                value={editDesc}
                                onChange={(e) => setEditDesc(e.target.value)}
                                rows={8}
                                data-testid="edit-post-desc"
                                placeholder="Idea description"
                            />
                        </div>
                        <div>
                            <Label htmlFor="edit-cat">Category</Label>
                            <Select value={editCat} onValueChange={setEditCat}>
                                <SelectTrigger data-testid="edit-post-category">
                                    <SelectValue placeholder="Pick a category" />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {categories.map((c) => (
                                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeEdit} data-testid="edit-post-cancel">Cancel</Button>
                        <Button onClick={saveEdit} disabled={saving} className="brand-button" data-testid="edit-post-save">
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function CategoriesTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState("");
    const [icon, setIcon] = useState("");
    const [desc, setDesc] = useState("");
    const [order, setOrder] = useState(0);
    const [enabled, setEnabled] = useState(true);

    const load = async () => {
        const { data } = await api.get("/admin/categories");
        setItems(data);
    };
    useEffect(() => { load(); }, []);

    const reset = () => { setEditing(null); setName(""); setIcon(""); setDesc(""); setOrder(0); setEnabled(true); };
    const startEdit = (c) => { setEditing(c.id); setName(c.name); setIcon(c.icon || ""); setDesc(c.description || ""); setOrder(c.order || 0); setEnabled(c.enabled); };
    const save = async () => {
        if (!name.trim()) return;
        const body = { name, icon, description: desc, order: parseInt(order) || 0, enabled };
        if (editing) await api.patch(`/admin/categories/${editing}`, body);
        else await api.post("/admin/categories", body);
        toast.success("Saved"); reset(); load();
    };
    const del = async (c) => { if (!window.confirm(`Delete ${c.name}?`)) return; await api.delete(`/admin/categories/${c.id}`); load(); };

    return (
        <div data-testid="admin-categories-tab" className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-2">
                {items.map((c) => (
                    <div key={c.id} className="rounded-xl border border-border bg-card p-4 flex items-center gap-3" data-testid={`cat-row-${c.slug}`}>
                        <div className="font-bold">{c.name}</div>
                        <div className="text-xs text-muted-foreground truncate flex-1">{c.description}</div>
                        <span className={`text-xs font-bold ${c.enabled ? "text-emerald-500" : "text-zinc-500"}`}>{c.enabled ? "ON" : "OFF"}</span>
                        <Button size="sm" variant="ghost" onClick={() => startEdit(c)} data-testid={`edit-cat-${c.slug}`}><Pencil size={14} /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(c)} data-testid={`delete-cat-${c.slug}`}><Trash size={14} /></Button>
                    </div>
                ))}
            </div>
            <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="admin-category-form">
                <h3 className="font-display font-bold text-lg">{editing ? "Edit" : "New"} category</h3>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="cat-name" />
                <Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="Icon (Phosphor name, e.g. Lightbulb)" data-testid="cat-icon" />
                <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Short description" data-testid="cat-desc" />
                <Input value={order} onChange={(e) => setOrder(e.target.value)} type="number" placeholder="Order" data-testid="cat-order" />
                <div className="flex items-center justify-between">
                    <Label className="m-0">Enabled</Label>
                    <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="cat-enabled" />
                </div>
                <div className="flex gap-2">
                    <Button onClick={save} className="brand-button flex-1" data-testid="cat-save"><Plus size={14} className="mr-1" />{editing ? "Save" : "Create"}</Button>
                    {editing && <Button variant="outline" onClick={reset} data-testid="cat-cancel">Cancel</Button>}
                </div>
            </div>
        </div>
    );
}

const PLAN_LABELS = {
    pro_monthly: "Pro",
    boost_post: "Boost",
    credits_pack: "AI Credits",
    unlock_categories: "Unlock",
    sell_idea: "Sell idea",
};

const CURRENCY_SYMBOLS = {
    USD: "$", EUR: "€", GBP: "£", INR: "₹", JPY: "¥", CAD: "CA$",
    AUD: "A$", SGD: "S$", NGN: "₦",
};

function fmtMoney(amount, currency) {
    const sym = CURRENCY_SYMBOLS[(currency || "USD").toUpperCase()] || "";
    const a = Number(amount || 0);
    const display = a >= 100 ? a.toFixed(0) : a.toFixed(2);
    return `${sym}${display}`;
}

function RevenueSummary() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const load = useCallback(() => {
        setErr(null);
        api.get("/admin/revenue", { timeout: 15000 })
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.message || "Failed to load revenue"));
    }, []);
    useEffect(() => { load(); }, [load]);

    if (err) {
        return (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm mb-6" data-testid="revenue-error">
                Couldn't load revenue: {err}
                <Button size="sm" className="ml-3" onClick={load} data-testid="revenue-retry">Retry</Button>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-28 rounded-2xl border border-border bg-muted/30 animate-pulse" />
                ))}
            </div>
        );
    }

    // Pick the largest-revenue currency to surface as the headline.
    const headlineCurrency =
        data.revenue_by_currency && data.revenue_by_currency[0];
    const totalSuccessfulPayments = data.successful_payments || 0;
    const totalPayingUsers = data.paying_users || 0;
    const avgRevenuePerPayment =
        headlineCurrency && totalSuccessfulPayments
            ? headlineCurrency.amount / totalSuccessfulPayments
            : 0;

    return (
        <div data-testid="revenue-summary">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="rounded-2xl border border-border bg-gradient-to-br from-[#6C63FF]/10 to-transparent p-5" data-testid="kpi-total-revenue">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Total revenue</div>
                    <div className="font-display font-black text-3xl tracking-tighter">
                        {headlineCurrency ? fmtMoney(headlineCurrency.amount, headlineCurrency.currency) : "—"}
                    </div>
                    {data.revenue_by_currency.length > 1 && (
                        <div className="text-[11px] text-muted-foreground mt-1">
                            + {data.revenue_by_currency.slice(1).map((r) => `${fmtMoney(r.amount, r.currency)}`).join(" + ")}
                        </div>
                    )}
                </div>
                <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-paying-users">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Paying users</div>
                    <div className="font-display font-black text-3xl tracking-tighter">{totalPayingUsers}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">unique customers</div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-successful-payments">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Successful payments</div>
                    <div className="font-display font-black text-3xl tracking-tighter">{totalSuccessfulPayments}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{data.initiated_only || 0} initiated, never completed</div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-arpu">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Avg / payment</div>
                    <div className="font-display font-black text-3xl tracking-tighter">
                        {headlineCurrency
                            ? fmtMoney(avgRevenuePerPayment, headlineCurrency.currency)
                            : "—"}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">across {headlineCurrency?.currency || "—"} txns</div>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4 mb-6">
                <div className="rounded-2xl border border-border bg-card p-5">
                    <h3 className="font-display font-bold text-lg mb-4">Revenue by plan</h3>
                    {data.revenue_by_plan.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No paid plans yet.</div>
                    ) : (
                        <div className="space-y-2" data-testid="revenue-by-plan">
                            {data.revenue_by_plan.map((r, i) => (
                                <div key={i} className="flex items-center justify-between gap-3 border-b border-border last:border-0 pb-2 last:pb-0">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="px-2 py-0.5 rounded-md bg-[#6C63FF]/15 text-[#6C63FF] text-xs font-bold whitespace-nowrap">
                                            {PLAN_LABELS[r.plan] || r.plan}
                                        </span>
                                        <span className="text-xs text-muted-foreground">{r.count} txn{r.count === 1 ? "" : "s"}</span>
                                    </div>
                                    <div className="font-mono text-sm font-bold">{fmtMoney(r.amount, r.currency)} {r.currency}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="rounded-2xl border border-border bg-card p-5">
                    <h3 className="font-display font-bold text-lg mb-4">Last 30 days</h3>
                    {data.last_30d.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No payments in the last 30 days.</div>
                    ) : (
                        <div className="space-y-2" data-testid="revenue-30d">
                            {data.last_30d.map((r, i) => (
                                <div key={i} className="flex items-center justify-between gap-3 border-b border-border last:border-0 pb-2 last:pb-0">
                                    <div className="text-sm font-medium">{r.currency}</div>
                                    <div className="text-xs text-muted-foreground">{r.count} payment{r.count === 1 ? "" : "s"}</div>
                                    <div className="font-mono text-sm font-bold">{fmtMoney(r.amount, r.currency)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function PaymentsTable() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("all");
    const [plan, setPlan] = useState("all");
    const [q, setQ] = useState("");
    const [skip, setSkip] = useState(0);
    const [busy, setBusy] = useState(null);
    const limit = 20;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status, plan, q: q || undefined, limit, skip };
            const { data: r } = await api.get("/admin/payments", { params });
            setData(r);
        } catch {
            toast.error("Couldn't load payments");
        }
        setLoading(false);
    }, [status, plan, q, skip]);

    useEffect(() => { load(); }, [load]);

    const reconcile = async (sid) => {
        setBusy(sid);
        try {
            await api.post(`/admin/payments/${sid}/reconcile`);
            toast.success("Reconciled — user's plan activated");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reconcile failed");
        }
        setBusy(null);
    };

    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="payments-table">
            <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                    <h3 className="font-display font-bold text-lg">Transactions</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{data.total} matching transaction{data.total === 1 ? "" : "s"}</p>
                </div>
                <div className="flex-1" />
                <Input
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setSkip(0); }}
                    placeholder="Search user email / name…"
                    className="w-56"
                    data-testid="payments-search"
                />
                <Select value={status} onValueChange={(v) => { setStatus(v); setSkip(0); }}>
                    <SelectTrigger className="w-36" data-testid="payments-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="reconcile">Reconcile</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={plan} onValueChange={(v) => { setPlan(v); setSkip(0); }}>
                    <SelectTrigger className="w-40" data-testid="payments-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All plans</SelectItem>
                        <SelectItem value="pro_monthly">Pro</SelectItem>
                        <SelectItem value="boost_post">Boost</SelectItem>
                        <SelectItem value="credits_pack">AI Credits</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-3 py-2">Date</th>
                            <th className="text-left px-3 py-2">User</th>
                            <th className="text-left px-3 py-2">Plan</th>
                            <th className="text-right px-3 py-2">Amount</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-right px-3 py-2">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</td></tr>
                        ) : data.items.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="payments-empty">
                                No transactions match these filters yet.
                            </td></tr>
                        ) : data.items.map((r) => (
                            <tr key={r.session_id || r.id} className="border-t border-border" data-testid={`tx-${r.session_id || r.id}`}>
                                <td className="px-3 py-3 whitespace-nowrap text-xs">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                                <td className="px-3 py-3">
                                    <div className="font-medium text-sm">{r.user_name || "—"}</div>
                                    <div className="text-[11px] text-muted-foreground">{r.user_email}</div>
                                    {r.user_country && <div className="text-[10px] text-muted-foreground">{r.user_country}</div>}
                                </td>
                                <td className="px-3 py-3">
                                    <span className="px-2 py-0.5 rounded-md bg-[#6C63FF]/15 text-[#6C63FF] text-xs font-bold">
                                        {PLAN_LABELS[r.plan] || r.plan}
                                    </span>
                                </td>
                                <td className="px-3 py-3 text-right font-mono text-sm">{fmtMoney(r.amount, r.currency)} {(r.currency || "").toUpperCase()}</td>
                                <td className="px-3 py-3">
                                    {(() => {
                                        // Use the backend-derived display_status so the UI mirrors
                                        // the real lifecycle (paid / reconcile / pending /
                                        // cancelled / failed) without recomputing client-side.
                                        const ds = r.display_status || r.payment_status || "—";
                                        const STYLES = {
                                            paid:       "bg-emerald-500/15 text-emerald-600",
                                            reconcile:  "bg-amber-500/15 text-amber-600",
                                            pending:    "bg-sky-500/15 text-sky-600",
                                            cancelled:  "bg-zinc-500/15 text-zinc-500",
                                            failed:     "bg-red-500/15 text-red-600",
                                        };
                                        const LABELS = {
                                            paid: "Paid",
                                            reconcile: "Reconcile",
                                            pending: "Pending",
                                            cancelled: "Cancelled",
                                            failed: "Failed",
                                        };
                                        return (
                                            <span
                                                className={`px-2 py-0.5 rounded-md text-xs font-bold ${STYLES[ds] || "bg-zinc-500/15 text-zinc-600"}`}
                                                data-testid={`tx-status-${r.session_id}`}
                                            >
                                                {LABELS[ds] || ds}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className="px-3 py-3 text-right">
                                    {r.display_status === "reconcile" && r.session_id && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => reconcile(r.session_id)}
                                            disabled={busy === r.session_id}
                                            data-testid={`reconcile-${r.session_id}`}
                                        >
                                            {busy === r.session_id ? "…" : "Reconcile"}
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between mt-4">
                <div className="text-xs text-muted-foreground">
                    Showing {data.items.length === 0 ? 0 : skip + 1}–{skip + data.items.length} of {data.total}
                </div>
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={skip === 0}
                        onClick={() => setSkip(Math.max(0, skip - limit))}
                        data-testid="payments-prev"
                    >Prev</Button>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={skip + limit >= data.total}
                        onClick={() => setSkip(skip + limit)}
                        data-testid="payments-next"
                    >Next</Button>
                </div>
            </div>
        </div>
    );
}

function PaymentsTab() {
    const [resetting, setResetting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const handleReset = async () => {
        const phrase = window.prompt(
            "This will permanently delete EVERY row in payment_transactions " +
            "(revenue, transaction logs, KPIs). User subscriptions and post " +
            "boost flags are NOT touched.\n\nType RESET to confirm:",
        );
        if (phrase !== "RESET") {
            if (phrase !== null) toast.error("Reset cancelled — phrase did not match.");
            return;
        }
        setResetting(true);
        try {
            const { data } = await api.post("/admin/payments/reset");
            toast.success(`Cleared ${data.deleted} transactions. Revenue is now £0.`);
            setRefreshKey((k) => k + 1);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Reset failed";
            toast.error(typeof msg === "string" ? msg : "Reset failed");
        } finally {
            setResetting(false);
        }
    };

    return (
        <div data-testid="payments-tab" className="space-y-2">
            <div className="flex items-center justify-end mb-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                    disabled={resetting}
                    data-testid="reset-payments-btn"
                    className="border-red-500/40 text-red-600 hover:bg-red-500/10 hover:text-red-700"
                >
                    {resetting ? "Resetting…" : "Reset revenue & transactions"}
                </Button>
            </div>
            <RevenueSummary key={`rev-${refreshKey}`} />
            <PaymentsTable key={`tbl-${refreshKey}`} />
        </div>
    );
}


// ───────────────── Storage tab ─────────────────
// Each Emergent custom-domain deployment has a finite storage budget.
// This tab surfaces the actual byte usage (uploads + Mongo) so the owner
// can spot growth trends and plan upgrades before hitting the limit.

const STORAGE_QUOTA_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB default — adjust per Emergent plan.

function fmtBytes(n) {
    if (!n || n < 0) return "0 B";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function StorageBar({ used, total, label, color = "#6C63FF" }) {
    const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
    const danger = pct > 85;
    return (
        <div>
            <div className="flex items-baseline justify-between mb-1.5">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
                <div className={`text-xs font-mono ${danger ? "text-red-500 font-bold" : ""}`}>
                    {fmtBytes(used)} / {fmtBytes(total)} ({pct.toFixed(1)}%)
                </div>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                    className="h-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: danger ? "#ef4444" : color }}
                />
            </div>
        </div>
    );
}

function StorageTab() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const load = useCallback(() => {
        setErr(null);
        api.get("/admin/storage", { timeout: 15000 })
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.message || "Failed to load storage stats"));
    }, []);
    useEffect(() => { load(); }, [load]);

    if (err) {
        return (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm" data-testid="storage-error">
                Couldn't load storage stats: {err}
                <Button size="sm" className="ml-3" onClick={load} data-testid="storage-retry">Retry</Button>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="grid grid-cols-2 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-28 rounded-2xl border border-border bg-muted/30 animate-pulse" />
                ))}
            </div>
        );
    }

    const obj = data.object_storage || {};
    const mongo = data.mongo || {};
    const totalUsed = (obj.active_bytes || 0) + (mongo.data_bytes || 0);

    return (
        <div data-testid="storage-tab" className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="rounded-2xl border border-border bg-gradient-to-br from-[#6C63FF]/10 to-transparent p-5" data-testid="kpi-storage-total">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Combined usage</div>
                    <div className="font-display font-black text-3xl tracking-tighter">{fmtBytes(totalUsed)}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">uploads + database</div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-active-files">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Active files</div>
                    <div className="font-display font-black text-3xl tracking-tighter">{obj.active_files || 0}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{fmtBytes(obj.active_bytes)} total</div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-deleted-files">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Soft-deleted</div>
                    <div className="font-display font-black text-3xl tracking-tighter">{obj.deleted_files || 0}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{fmtBytes(obj.deleted_bytes)} reclaimable</div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-mongo">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Database</div>
                    <div className="font-display font-black text-3xl tracking-tighter">{fmtBytes(mongo.data_bytes)}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{mongo.documents?.toLocaleString() || 0} docs / {mongo.collections || 0} cols</div>
                </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="storage-bars">
                <h3 className="font-display font-bold text-lg">Quota usage</h3>
                <p className="text-xs text-muted-foreground -mt-2">
                    Bars assume a {fmtBytes(STORAGE_QUOTA_BYTES)} default plan. Adjust the quota in
                    <span className="font-mono"> STORAGE_QUOTA_BYTES </span> if your Emergent plan differs.
                </p>
                <StorageBar
                    used={obj.active_bytes || 0}
                    total={STORAGE_QUOTA_BYTES}
                    label="Object storage (uploads)"
                    color="#6C63FF"
                />
                <StorageBar
                    used={mongo.storage_bytes || 0}
                    total={STORAGE_QUOTA_BYTES}
                    label="MongoDB (on-disk)"
                    color="#FF8A00"
                />
                <StorageBar
                    used={totalUsed}
                    total={STORAGE_QUOTA_BYTES}
                    label="Combined"
                    color="#10b981"
                />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-border bg-card p-5">
                    <h3 className="font-display font-bold text-lg mb-4">By content type</h3>
                    {(obj.by_content_type || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">No uploads yet.</div>
                    ) : (
                        <div className="space-y-2" data-testid="storage-by-type">
                            {(obj.by_content_type || []).map((r, i) => (
                                <div key={i} className="flex items-center justify-between gap-3 border-b border-border last:border-0 pb-2 last:pb-0">
                                    <span className="text-sm font-medium">{r.content_type}</span>
                                    <span className="text-xs text-muted-foreground">{r.count} file{r.count === 1 ? "" : "s"}</span>
                                    <span className="font-mono text-sm font-bold">{fmtBytes(r.bytes)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="rounded-2xl border border-border bg-card p-5">
                    <h3 className="font-display font-bold text-lg mb-4">Top uploaders</h3>
                    {(obj.top_uploaders || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">No uploaders yet.</div>
                    ) : (
                        <div className="space-y-2" data-testid="storage-top-uploaders">
                            {(obj.top_uploaders || []).map((r, i) => (
                                <div key={i} className="flex items-center justify-between gap-3 border-b border-border last:border-0 pb-2 last:pb-0">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium truncate">{r.user_name || "—"}</div>
                                        <div className="text-[11px] text-muted-foreground truncate">{r.user_email}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground">{r.count}</div>
                                    <div className="font-mono text-sm font-bold">{fmtBytes(r.bytes)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="text-xs text-muted-foreground">
                <Button size="sm" variant="outline" onClick={load} data-testid="storage-refresh">Refresh</Button>
                <span className="ml-3">Mongo data shown is the active DB working set; storage on disk includes indexes and overhead.</span>
            </div>
        </div>
    );
}


function BroadcastsTab() {
    const [audience, setAudience] = useState("all");
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [deepLink, setDeepLink] = useState("");
    const [sending, setSending] = useState(false);
    const [recipientPreview, setRecipientPreview] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const { data } = await api.get("/admin/broadcasts?limit=30");
            setHistory(data.items || []);
        } catch { /* ignore */ }
        setHistoryLoading(false);
    };

    useEffect(() => { loadHistory(); }, []);

    // Whenever the audience changes, fire a dry-run to show the admin
    // how many recipients they're about to hit before they click Send.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post("/admin/broadcasts", {
                    audience, title: "x", body: "x", dry_run: true,
                });
                if (!cancelled) setRecipientPreview(data);
            } catch {
                if (!cancelled) setRecipientPreview(null);
            }
        })();
        return () => { cancelled = true; };
    }, [audience]);

    const reset = () => { setTitle(""); setBody(""); setDeepLink(""); };

    const submit = async () => {
        if (!title.trim() || !body.trim()) {
            toast.error("Title and body are required"); return;
        }
        const audienceLabel = {
            all: "ALL users",
            free: "FREE users only",
            pro_local: "PRO · LOCAL users only",
            pro_global: "PRO · GLOBAL users only",
        }[audience];
        const recipientsLine = recipientPreview
            ? `${recipientPreview.recipients_count} device${recipientPreview.recipients_count === 1 ? "" : "s"} (of ${recipientPreview.audience_user_count} ${audience} users)`
            : "all matching users";
        if (!window.confirm(`Send push to ${audienceLabel}?\n\n${recipientsLine}\n\nThis cannot be undone.`)) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/broadcasts", {
                audience, title: title.trim(), body: body.trim(),
                deep_link: deepLink.trim() || null,
            });
            const b = data.broadcast;
            toast.success(`Sent: ${b.sent} · Failed: ${b.failed} · Reached ${b.recipients_count} device(s)`);
            reset();
            loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to send");
        } finally {
            setSending(false);
        }
    };

    const audienceDot = {
        all:        "bg-foreground",
        free:       "bg-muted-foreground",
        pro_local:  "bg-[#6C63FF]",
        pro_global: "bg-[#FF8A00]",
    }[audience] || "bg-muted-foreground";

    return (
        <div className="space-y-6" data-testid="broadcasts-tab">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
                {/* ── Composer ─────────────────────────────── */}
                <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display font-black text-xl tracking-tight">Send push notification</h2>
                        {recipientPreview != null && (
                            <span
                                className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground"
                                data-testid="broadcast-recipient-count"
                            >
                                <span className={`h-2 w-2 rounded-full ${audienceDot}`} />
                                {recipientPreview.recipients_count} device(s)
                                <span className="text-[10px] opacity-70">/ {recipientPreview.audience_user_count} user(s)</span>
                            </span>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Audience</label>
                        <select
                            value={audience}
                            onChange={(e) => setAudience(e.target.value)}
                            data-testid="broadcast-audience"
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/40"
                        >
                            <option value="all">All users</option>
                            <option value="free">Free users only</option>
                            <option value="pro_local">Pro · Local users</option>
                            <option value="pro_global">Pro · Global users (incl. admins)</option>
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Title <span className="opacity-50">({title.length}/100)</span>
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            maxLength={100}
                            placeholder="New version available!"
                            data-testid="broadcast-title"
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/40"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Body <span className="opacity-50">({body.length}/240)</span>
                        </label>
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            maxLength={240}
                            rows={3}
                            placeholder="Update to v1.2 to grab the new AI Idea Validator + dark mode."
                            data-testid="broadcast-body"
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/40"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Deep link <span className="opacity-50">(optional)</span>
                        </label>
                        <input
                            type="text"
                            value={deepLink}
                            onChange={(e) => setDeepLink(e.target.value)}
                            maxLength={300}
                            placeholder="/subscription   or   /posts/{id}"
                            data-testid="broadcast-deeplink"
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/40"
                        />
                        <p className="text-[10px] text-muted-foreground">
                            Sent in <code>data.url</code> — the mobile app routes there when the user taps the push.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                        <Button
                            onClick={submit}
                            disabled={sending || !title.trim() || !body.trim()}
                            data-testid="broadcast-send"
                            className="bg-[#FF8A00] hover:bg-[#FF8A00]/90 text-white font-bold"
                        >
                            {sending ? "Sending…" : "Send push"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={reset}
                            disabled={sending || (!title && !body && !deepLink)}
                            data-testid="broadcast-clear"
                        >
                            Clear
                        </Button>
                    </div>
                </div>

                {/* ── Live preview ─────────────────────────── */}
                <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
                    <h2 className="font-display font-black text-xl tracking-tight">Preview</h2>
                    <div className="rounded-2xl border border-border bg-background p-3 flex gap-3">
                        <img
                            src="/logo-192.png"
                            alt=""
                            className="h-10 w-10 rounded-lg object-cover shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-sm truncate">
                                    {title || "Notification title"}
                                </span>
                                <span className="text-[10px] text-muted-foreground shrink-0">now</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-3">
                                {body || "Body text shows here. iOS truncates around 178 chars on the lock screen, Android collapses past two lines."}
                            </p>
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-2">
                        <div>📲 Sent via Expo Push → APNs / FCM.</div>
                        <div>🚫 Users without a registered device (web-only) are skipped silently.</div>
                        <div>🧹 Stale tokens auto-purged on Expo&apos;s <code>DeviceNotRegistered</code>.</div>
                    </div>
                </div>
            </div>

            {/* ── History ──────────────────────────────────── */}
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <h3 className="font-semibold text-sm">Recent broadcasts</h3>
                    <button
                        onClick={loadHistory}
                        data-testid="broadcasts-refresh"
                        className="text-xs text-muted-foreground hover:text-foreground"
                    >
                        ↻ Refresh
                    </button>
                </div>
                {historyLoading ? (
                    <div className="p-6 text-sm text-muted-foreground">Loading…</div>
                ) : history.length === 0 ? (
                    <div className="p-6 text-sm text-muted-foreground" data-testid="broadcasts-empty">
                        No broadcasts sent yet.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="broadcasts-history">
                        <thead className="text-left text-xs uppercase text-muted-foreground bg-muted/30">
                            <tr>
                                <th className="px-4 py-2 font-semibold">Sent at</th>
                                <th className="px-4 py-2 font-semibold">Audience</th>
                                <th className="px-4 py-2 font-semibold">Title</th>
                                <th className="px-4 py-2 font-semibold text-right">Devices</th>
                                <th className="px-4 py-2 font-semibold text-right">Sent</th>
                                <th className="px-4 py-2 font-semibold text-right">Failed</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {history.map((b) => (
                                <tr key={b.id} data-testid={`broadcast-${b.id}`}>
                                    <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                                        {new Date(b.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-2 text-xs">
                                        <span className="px-2 py-0.5 rounded-md bg-muted text-foreground font-semibold uppercase">
                                            {b.audience.replace("_", " ")}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2">
                                        <div className="font-semibold truncate max-w-[280px]" title={b.title}>{b.title}</div>
                                        <div className="text-xs text-muted-foreground truncate max-w-[280px]" title={b.body}>{b.body}</div>
                                    </td>
                                    <td className="px-4 py-2 text-right tabular-nums">{b.recipients_count}</td>
                                    <td className="px-4 py-2 text-right tabular-nums text-green-500">{b.sent}</td>
                                    <td className="px-4 py-2 text-right tabular-nums text-red-500">{b.failed}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}


export default function Admin() {
    return (
        <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-7xl mx-auto" data-testid="admin-page">
            <header className="mb-8 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#6C63FF] grid place-items-center text-white">
                    <ShieldStar size={22} weight="fill" />
                </div>
                <div>
                    <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter brand-text">Admin dashboard</h1>
                    <p className="text-sm text-muted-foreground">Full platform control.</p>
                </div>
            </header>
            <Tabs defaultValue="stats">
                <TabsList data-testid="admin-tabs">
                    <TabsTrigger value="stats" data-testid="tab-stats">Overview</TabsTrigger>
                    <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
                    <TabsTrigger value="posts" data-testid="tab-posts">Posts</TabsTrigger>
                    <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
                    <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
                    <TabsTrigger value="rescue" data-testid="tab-rescue">IAP Rescue</TabsTrigger>
                    <TabsTrigger value="storage" data-testid="tab-storage">Storage</TabsTrigger>
                    <TabsTrigger value="broadcasts" data-testid="tab-broadcasts">Notifications</TabsTrigger>
                    <TabsTrigger value="health" data-testid="tab-health">System Health</TabsTrigger>
                </TabsList>
                <TabsContent value="stats" className="mt-6"><StatsTab /></TabsContent>
                <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
                <TabsContent value="posts" className="mt-6"><PostsTab /></TabsContent>
                <TabsContent value="categories" className="mt-6"><CategoriesTab /></TabsContent>
                <TabsContent value="payments" className="mt-6"><PaymentsTab /></TabsContent>
                <TabsContent value="rescue" className="mt-6"><PendingVerifyTab /></TabsContent>
                <TabsContent value="storage" className="mt-6"><StorageTab /></TabsContent>
                <TabsContent value="broadcasts" className="mt-6"><BroadcastsTab /></TabsContent>
                <TabsContent value="health" className="mt-6"><SystemHealthTab /></TabsContent>
            </Tabs>
        </div>
    );
}
